import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../config";
import { RefreshCw, AlertTriangle, CheckCircle, Loader2, DollarSign, TrendingUp, TrendingDown, Save, Globe } from "lucide-react";

const PACKAGES = [
  { id: "allin_2026_monthly", name: "Všetko v jednom 2026", base: 79, type: "subscription" },
  { id: "allin_2026_prepaid", name: "Všetko v jednom 2026 (do 31. 12.)", base: 590, type: "one_time" },
  { id: "pro_monthly", name: "Pro", base: 49, type: "subscription" },
  { id: "business_monthly", name: "Business", base: 169, type: "subscription" },
  { id: "enterprise_monthly", name: "Enterprise", base: 399, type: "subscription" },
  { id: "addon_brand_monitor", name: "Brand Monitor", base: 19, type: "subscription" },
  { id: "addon_competitor_spy", name: "Competitor Spy", base: 29, type: "subscription" },
  { id: "addon_attribution", name: "AI Attribution", base: 39, type: "subscription" },
  { id: "addon_autopilot", name: "SEO Autopilot", base: 79, type: "subscription" },
  { id: "addon_ads_creator", name: "Ads Creator", base: 69, type: "subscription" },
  { id: "addon_blog_agent", name: "Blog Agent", base: 25, type: "one_time" },
  { id: "addon_fix_seo", name: "Fix My SEO", base: 9, type: "one_time" },
];

const KEY_CURRENCIES = ["USD", "GBP", "CZK", "PLN", "HUF", "SEK", "NOK", "DKK", "RON", "BGN", "TRY", "JPY", "KRW", "CNY", "INR", "UAH", "SAR", "IDR"];

export default function AdminPricingTab() {
  const [rates, setRates] = useState({});
  const [alerts, setAlerts] = useState([]);
  const [fetchedAt, setFetchedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [overrides, setOverrides] = useState({});
  const [livePkgs, setLivePkgs] = useState({});
  const [saving, setSaving] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [hideAdminBypass, setHideAdminBypass] = useState(() => {
    try { return localStorage.getItem("admin_hide_bypass") === "1"; } catch { return false; }
  });
  const [txStats, setTxStats] = useState({ all_count: 0, admin_bypass_count: 0, real_customer_count: 0, real_revenue_eur: 0 });

  useEffect(() => { loadData(); }, []);  // eslint-disable-line
  useEffect(() => { loadTransactions(); }, [hideAdminBypass]);  // eslint-disable-line
  useEffect(() => {
    try { localStorage.setItem("admin_hide_bypass", hideAdminBypass ? "1" : "0"); } catch { /* noop */ }
  }, [hideAdminBypass]);

  const loadTransactions = async () => {
    const r = await axios
      .get(`${API}/admin/transactions`, { params: { hide_admin_bypass: hideAdminBypass } })
      .catch(() => ({ data: { transactions: [] } }));
    setTransactions(r.data.transactions || []);
    setTxStats({
      all_count: r.data.all_count || 0,
      admin_bypass_count: r.data.admin_bypass_count || 0,
      real_customer_count: r.data.real_customer_count || 0,
      real_revenue_eur: r.data.real_revenue_eur || 0,
    });
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [ratesRes, alertsRes, overridesRes, pkgRes] = await Promise.all([
        axios.get(`${API}/exchange-rates`),
        axios.get(`${API}/exchange-rates/alerts`),
        axios.get(`${API}/admin/price-overrides`).catch(() => ({ data: { overrides: {} } })),
        axios.get(`${API}/payments/packages`).catch(() => ({ data: { packages: [] } })),
      ]);
      setLivePkgs(Object.fromEntries((pkgRes.data.packages || []).map((p) => [p.id, p.amount])));
      setRates(ratesRes.data.rates || {});
      setFetchedAt(ratesRes.data.fetched_at);
      setAlerts(alertsRes.data.alerts || []);
      setOverrides(overridesRes.data.overrides || {});
      await loadTransactions();
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  const refreshRates = async () => {
    setRefreshing(true);
    try {
      const res = await axios.post(`${API}/exchange-rates/refresh`);
      setRates(res.data.rates || {});
      setFetchedAt(res.data.fetched_at);
      if (res.data.alerts?.length > 0) setAlerts(res.data.alerts);
    } catch (e) { console.error(e); }
    setRefreshing(false);
  };

  const convertPrice = (eurAmount, currency) => {
    // Check for manual override first
    const key = `${currency}`;
    if (overrides[key]) return overrides[key];
    
    const rate = rates[currency];
    if (!rate) return eurAmount;
    const converted = eurAmount * rate;
    if (["JPY", "KRW", "IDR"].includes(currency)) return Math.round(converted / 100) * 100;
    if (["HUF", "CZK"].includes(currency)) return Math.round(converted / 10) * 10;
    return Math.round(converted);
  };

  const handleOverride = (currency, packageId, value) => {
    const key = `${currency}_${packageId}`;
    setOverrides(prev => ({ ...prev, [key]: value === "" ? undefined : parseFloat(value) }));
  };

  const saveOverrides = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/price-overrides`, { overrides }, { headers: { "X-Admin-Token": localStorage.getItem("admin_token") || "" } });
      toast.success("Ceny uložené — pokladňa ich použije hneď");
    } catch (e) { toast.error("Uloženie cien zlyhalo"); }
    setSaving(false);
  };

  const formatCurrency = (amount, currency) => {
    const symbols = { USD: "$", GBP: "£", CZK: "Kč", PLN: "zł", HUF: "Ft", SEK: "kr", NOK: "kr", DKK: "kr", RON: "lei", BGN: "лв", TRY: "₺", JPY: "¥", KRW: "₩", CNY: "¥", INR: "₹", UAH: "₴", SAR: "﷼", IDR: "Rp" };
    const sym = symbols[currency] || currency;
    const pre = ["USD", "GBP", "JPY", "KRW", "CNY", "INR"].includes(currency);
    return pre ? `${sym}${amount.toLocaleString()}` : `${amount.toLocaleString()} ${sym}`;
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>;

  return (
    <div className="space-y-8" data-testid="admin-pricing-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold">Kurzy & Ceny</h2>
          <p className="text-sm text-gray-500">Posledná aktualizácia: {fetchedAt ? new Date(fetchedAt).toLocaleString("sk") : "—"}</p>
        </div>
        <button onClick={refreshRates} disabled={refreshing} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-bold rounded-lg hover:bg-blue-500 disabled:opacity-50" data-testid="refresh-rates-btn">
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? "Aktualizujem..." : "Aktualizovať kurzy"}
        </button>
      </div>

      {/* Alerts */}
      {alerts.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4" data-testid="rate-alerts">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            <span className="font-bold text-amber-800">Upozornenie: Zmena kurzu &gt;5%</span>
          </div>
          {alerts.map((a, i) => (
            <div key={i} className="flex items-center gap-3 text-sm text-amber-700 ml-7">
              <span className="font-mono font-bold">{a.currency}</span>
              <span>{a.old_rate?.toFixed(4)} → {a.new_rate?.toFixed(4)}</span>
              <span className={`font-bold ${a.change_pct > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                {a.change_pct > 0 ? <TrendingUp className="w-3 h-3 inline" /> : <TrendingDown className="w-3 h-3 inline" />} {a.change_pct}%
              </span>
              <span className="text-amber-500">— Skontrolujte ceny!</span>
            </div>
          ))}
        </div>
      )}

      {/* Key exchange rates */}
      <div>
        <h3 className="text-sm font-bold text-gray-500 uppercase mb-3">Aktuálne kurzy (EUR →)</h3>
        <div className="grid grid-cols-3 sm:grid-cols-6 lg:grid-cols-9 gap-2">
          {KEY_CURRENCIES.map(cur => (
            <div key={cur} className="bg-white border border-gray-200 rounded p-3 text-center" data-testid={`rate-${cur}`}>
              <p className="text-xs font-mono text-gray-400">{cur}</p>
              <p className="text-lg font-bold font-mono">{rates[cur]?.toFixed(cur === "JPY" || cur === "KRW" || cur === "IDR" ? 1 : 3) || "—"}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Price table per currency */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold text-gray-500 uppercase">Cenník vo všetkých menách</h3>
          <button onClick={saveOverrides} disabled={saving} className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white text-sm font-bold rounded-lg hover:bg-emerald-500 disabled:opacity-50" data-testid="save-overrides-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Uložiť úpravy
          </button>
        </div>
        <div className="overflow-x-auto bg-white border border-gray-200 rounded-lg">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3 font-bold text-gray-600">Balík</th>
                <th className="text-right px-4 py-3 font-bold text-gray-600">EUR</th>
                {KEY_CURRENCIES.slice(0, 8).map(cur => (
                  <th key={cur} className="text-right px-3 py-3 font-bold text-gray-500 text-xs">{cur}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {PACKAGES.map((p) => ({ ...p, base: livePkgs[p.id] ?? p.base })).map((pkg, i) => (
                <tr key={pkg.id} className={`border-b border-gray-100 ${i % 2 === 0 ? '' : 'bg-gray-50/50'}`}>
                  <td className="px-4 py-2.5">
                    <span className="font-medium">{pkg.name}</span>
                    <span className="text-xs text-gray-400 ml-2">{pkg.type === "one_time" ? "jednorázovo" : "/mes"}</span>
                  </td>
                  <td className="text-right px-4 py-2.5 font-bold font-mono" data-testid={`price-EUR-${pkg.id}`}>€{pkg.base}</td>
                  {KEY_CURRENCIES.slice(0, 8).map(cur => {
                    const autoPrice = convertPrice(pkg.base, cur);
                    const overrideKey = `${cur}_${pkg.id}`;
                    const hasOverride = overrides[overrideKey] !== undefined;
                    return (
                      <td key={cur} className="text-right px-2 py-1">
                        <input
                          type="number"
                          value={hasOverride ? overrides[overrideKey] : autoPrice}
                          onChange={(e) => handleOverride(cur, pkg.id, e.target.value)}
                          className={`w-20 text-right text-xs font-mono px-1 py-1 border rounded ${hasOverride ? 'border-amber-400 bg-amber-50' : 'border-gray-200'}`}
                          data-testid={`price-${cur}-${pkg.id}`}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-gray-400 mt-2">Žlté políčka = manuálne upravená cena. Prázdne = automatický prepočet z EUR kurzu.</p>
      </div>

      {/* Recent transactions */}
      <div>
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-3">
          <div>
            <h3 className="text-sm font-bold text-gray-500 uppercase">Posledné transakcie</h3>
            <div className="flex flex-wrap items-center gap-3 mt-2 text-xs" data-testid="tx-stats">
              <span className="text-gray-500">
                <span className="font-bold text-gray-700">{txStats.real_customer_count}</span> reálnych zákazníkov
              </span>
              <span className="text-gray-300">·</span>
              <span className="text-gray-500">
                <span className="font-bold text-purple-700">{txStats.admin_bypass_count}</span> admin-bypass
              </span>
              <span className="text-gray-300">·</span>
              <span className="text-gray-500">
                Reálne tržby: <span className="font-bold text-emerald-700">€{txStats.real_revenue_eur.toFixed(2)}</span>
              </span>
            </div>
          </div>
          <label className="inline-flex items-center gap-2 cursor-pointer select-none" data-testid="tx-hide-bypass-toggle">
            <input
              type="checkbox"
              checked={hideAdminBypass}
              onChange={(e) => setHideAdminBypass(e.target.checked)}
              className="sr-only peer"
            />
            <span className="relative w-10 h-5 bg-gray-200 peer-checked:bg-purple-600 rounded-full transition-colors">
              <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${hideAdminBypass ? "translate-x-5" : ""}`} />
            </span>
            <span className="text-xs font-medium text-gray-700">Skryť admin-bypass</span>
          </label>
        </div>
        {transactions.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-lg p-8 text-center text-gray-400">
            <DollarSign className="w-12 h-12 mx-auto mb-3 text-gray-200" />
            <p>Zatiaľ žiadne transakcie</p>
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-4 py-2 text-gray-500">Email</th>
                  <th className="text-left px-4 py-2 text-gray-500">Balík</th>
                  <th className="text-right px-4 py-2 text-gray-500">Suma</th>
                  <th className="text-center px-4 py-2 text-gray-500">Stav</th>
                  <th className="text-right px-4 py-2 text-gray-500">Dátum</th>
                </tr>
              </thead>
              <tbody>
                {transactions.slice(0, 20).map((tx, i) => (
                  <tr key={i} className="border-b border-gray-100" data-testid={`tx-row-${i}`}>
                    <td className="px-4 py-2 font-mono text-xs">
                      {tx.user_email || "—"}
                      {tx.admin_bypass && (
                        <span
                          className="ml-2 inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-purple-100 text-purple-700"
                          data-testid={`tx-bypass-badge-${i}`}
                          title="Admin bypass — neprinieslo reálne tržby"
                        >
                          ADMIN FREE
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 font-medium">{tx.package_name}</td>
                    <td className="text-right px-4 py-2 font-mono font-bold">{tx.amount} {tx.currency?.toUpperCase()}</td>
                    <td className="text-center px-4 py-2">
                      <span className={`text-xs px-2 py-1 rounded-full font-bold ${
                        tx.payment_status === "paid" ? "bg-emerald-100 text-emerald-700" :
                        tx.payment_status === "initiated" ? "bg-amber-100 text-amber-700" :
                        "bg-gray-100 text-gray-600"
                      }`}>
                        {tx.payment_status === "paid" ? "Zaplatené" : tx.payment_status === "initiated" ? "Začaté" : tx.payment_status}
                      </span>
                    </td>
                    <td className="text-right px-4 py-2 text-xs text-gray-500">{tx.created_at ? new Date(tx.created_at).toLocaleString("sk") : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
